import * as yup from 'yup';

import { getTranslatedMessage } from '@/i18n/utils';

import { NoTaxResidencyReasonCode } from '@/types/api/tax-info';

import {
  FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH,
  MAX_FOREIGN_NON_TAX_RESIDENCIES,
  NO_TAX_RESIDENCY_REASON_CODES,
} from './constants';

export function buildForeignNonTaxResidenciesValidator() {
  const foreignNonTaxResidencyBlockSchema = yup.object({
    countryCode: yup
      .string()
      .required(
        getTranslatedMessage(
          'taxInfoDrawer.question2.foreignNonTaxResidency.country.required',
        ),
      ),
    noTaxResidencyReasonCode: yup
      .mixed<NoTaxResidencyReasonCode>()
      .oneOf([...NO_TAX_RESIDENCY_REASON_CODES, NoTaxResidencyReasonCode.OTHER])
      .required(
        getTranslatedMessage(
          'taxInfoDrawer.question2.foreignNonTaxResidency.reason.required',
        ),
      ),
    noTaxResidencyReasonOther: yup.string().when('noTaxResidencyReasonCode', {
      is: NoTaxResidencyReasonCode.OTHER,
      then: schema =>
        schema
          .required(
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reasonOther.required',
            ),
          )
          .max(
            FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH,
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignNonTaxResidency.reasonOther.maxLength',
              { count: FOREIGN_NON_TAX_RESIDENCY_REASON_OTHER_MAX_LENGTH },
            ),
          ),
      otherwise: schema => schema.notRequired(),
    }),
  });

  return yup
    .array()
    .default([])
    .when(['hasForeignIndicia', 'isForeignTaxResident'], {
      is: (hasForeignIndicia: boolean, isForeignTaxResident: boolean) =>
        hasForeignIndicia === true && isForeignTaxResident === false,
      then: schema =>
        schema
          .of(foreignNonTaxResidencyBlockSchema)
          .min(
            1,
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignNonTaxResidency.required',
            ),
          )
          .max(MAX_FOREIGN_NON_TAX_RESIDENCIES)
          .test(
            'unique-foreign-non-tax-countries',
            getTranslatedMessage(
              'taxInfoDrawer.question2.foreignNonTaxResidency.country.duplicate',
            ),
            value => {
              const countryCodes = (value ?? [])
                .map(block => block?.countryCode)
                .filter(Boolean);

              return new Set(countryCodes).size === countryCodes.length;
            },
          ),
      otherwise: schema => schema.notRequired(),
    });
}
